/**
 * SPEC-08 §3.5 — `<ResultsLayout/>`.
 *
 * Two-column shell the results tabs can opt into so the tie-out / closing-
 * balance context lives in a fixed right rail at xl+ widths (visible
 * alongside the data, not stacked on top of it).
 *
 *   <ResultsLayout rail={<TieOutRail .../>}>
 *     {tab content}
 *   </ResultsLayout>
 *
 * Main column has `min-w-0` so wide tables size correctly inside flex/grid
 * (the single most-common defect when shipping a side rail naively). The
 * rail is `w-[260px]`, `hidden xl:block`, with a hairline left border.
 *
 * This SPEC ships the shell only; individual tabs opt in via their own
 * SPECs. EngagementResultsPage uses it without a rail by default (passing
 * `rail={null}`); the per-tab SPECs supply the rail content when ready.
 */

import { cn } from "@/lib/utils";

export interface ResultsLayoutProps {
  /**
   * Right-rail content. Pass `null` / `undefined` to render the main
   * column only (no border, no reserved space). Adopters wrap the rail
   * content in `<TieOutRail/>` for chrome.
   */
  rail?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function ResultsLayout({
  rail,
  children,
  className,
  "data-testid": testId,
}: ResultsLayoutProps) {
  return (
    <div
      data-testid={testId ?? "results-layout"}
      className={cn(
        rail ? "flex gap-6" : "block",
        className,
      )}
    >
      <div className="flex-1 min-w-0">{children}</div>
      {rail && (
        <aside className="hidden xl:block w-[260px] shrink-0 border-l pl-6">
          {rail}
        </aside>
      )}
    </div>
  );
}
